import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Clock3, Loader2 } from 'lucide-react'
import { authApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'

// The work session is a hard ceiling from login (or from the last
// deliberate renewal) — not a sliding window kept alive by ordinary
// activity, see the interceptor in lib/api.ts. This warns 2 minutes before
// the access token (ACCESS_TOKEN_EXPIRE_MINUTES, backend) lapses and offers
// the one way to extend it; ignoring the warning lets it expire regardless.
const WARNING_MS = 2 * 60 * 1000

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export default function SessionTimer() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { isAuthenticated, logout } = useAuthStore()
  const [showWarning, setShowWarning] = useState(false)
  const [remainingMs, setRemainingMs] = useState(0)

  const { data } = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: authApi.getSession,
    enabled: isAuthenticated,
    staleTime: Infinity,
    retry: false,
  })

  const expiresAt = data?.expires_at ? new Date(data.expires_at).getTime() : null

  const forceExpire = () => {
    setShowWarning(false)
    logout()
    navigate('/login', { replace: true })
    toast({ title: t('session.expired') })
  }

  const { mutate: renew, isPending: renewing } = useMutation({
    mutationFn: authApi.renewSession,
    onSuccess: (resp) => {
      queryClient.setQueryData(['auth', 'session'], { expires_at: resp.expires_at })
      setShowWarning(false)
    },
    onError: forceExpire,
  })

  // (Re)schedule the warning and the hard cutoff whenever the expiry
  // changes — on first load and after every renewal.
  useEffect(() => {
    if (!expiresAt) return
    const expireIn = expiresAt - Date.now()
    if (expireIn <= 0) {
      forceExpire()
      return
    }
    const warnTimer = window.setTimeout(() => setShowWarning(true), Math.max(0, expireIn - WARNING_MS))
    const expireTimer = window.setTimeout(forceExpire, expireIn)
    return () => {
      window.clearTimeout(warnTimer)
      window.clearTimeout(expireTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt])

  // Live countdown while the warning dialog is visible.
  useEffect(() => {
    if (!showWarning || !expiresAt) return
    const tick = () => setRemainingMs(Math.max(0, expiresAt - Date.now()))
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [showWarning, expiresAt])

  if (!isAuthenticated) return null

  return (
    <Dialog open={showWarning} onOpenChange={(open) => !open && setShowWarning(false)}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock3 className="w-5 h-5 text-giallo-dark" />
            {t('session.warningTitle')}
          </DialogTitle>
          <DialogDescription>{t('session.warningBody')}</DialogDescription>
        </DialogHeader>
        <p className="text-3xl font-bold tabular-nums text-center py-2" aria-live="polite">
          {formatCountdown(remainingMs)}
        </p>
        <DialogFooter>
          <Button onClick={() => renew()} disabled={renewing}>
            {renewing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t('session.renew')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
